import React, { useState, useMemo, useEffect } from "react";
import useSWR from "swr";
import EditIcon from "../../../svgs/edit.svg";
import CheckIcon from "@mui/icons-material/Check";
import CreateChargingPlanModal from "./CreateChargingPlanModal";
import SkeletonTable from "../../Common/SkeletonTable";
import ErrorCard from "../../Common/ErrorCard";
import EmptyCard from "../../Common/EmptyCard";
import PaginationBar from "../../Common/PaginationBar";
import { inventoryFetcher } from "../../../network/InventoryService";
import { DEFAULT_PAGE_SIZE } from "../../../utils/inventoryHelper";

const ChargingPlanList = ({ searchTerm, planStatus, onPlanCreated }) => {
  const [page, setPage] = useState(1);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const localUserType = JSON.parse(localStorage.getItem("currUserType"));

  useEffect(() => {
    setPage(1);
  }, [searchTerm, planStatus]);

  const apiUrl = useMemo(() => {
    const params = new URLSearchParams({
      page_index: page - 1,
      page_size: DEFAULT_PAGE_SIZE,
    });

    if (searchTerm) {
      params.append("search_term", searchTerm);
    }

    if (planStatus) {
      params.append("status", planStatus);
    }

    return `${
      process.env.REACT_APP_API_URL
    }/api/payment_speaker/v1/psb-scheme?${params.toString()}`;
  }, [page, searchTerm, planStatus]);

  const {
    data: response,
    error,
    mutate,
  } = useSWR(apiUrl, inventoryFetcher, {
    fallbackData: {},
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    errorRetryCount: 3,
    errorRetryInterval: 1000,
    refreshInterval: 5 * 60 * 1000,
  });

  const chargingPlanData = response?.data || [];
  const totalCount = response?.count || 0;
  const totalPages = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);
  const loading = !response && !error;

  const getStatusBadgeClass = (isActive) => {
    return isActive
      ? "plan-status-badge active"
      : "plan-status-badge deactivated";
  };

  const getStatusBadgeIconClass = (isActive) => {
    return isActive
      ? "plan-status-badge-icon active"
      : "plan-status-badge-icon deactivated";
  };

  const getStatusText = (isActive) => {
    return isActive ? "Active" : "Deactivated";
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === 0) return "-";
    return amount.toLocaleString();
  };

  const formatPlanType = (schemeType) => {
    switch (schemeType) {
      case "mixed":
        return "Mixed";
      case "full_upfront":
        return "Full Upfront";
      case "full_emi":
        return "EMI Only";
      case "free":
        return "Free";
      default:
        return schemeType;
    }
  };

  const formatEmiAmount = (emiAmount, emiCount) => {
    if (!emiAmount || emiAmount === 0) return "-";
    return `${emiAmount} x ${emiCount || 0}`;
  };

  const calculateTotalCharge = (upFrontAmount, emiAmount, emiCount) => {
    const upFront = upFrontAmount || 0;
    const totalEmi = (emiAmount || 0) * (emiCount || 0);
    const total = upFront + totalEmi;
    return total > 0 ? total : null;
  };

  const handleEditPlan = (plan) => {
    setSelectedPlan(plan);
    setShowEditModal(true);
  };

  const renderTableContent = () => {
    if (loading) {
      return (
        <SkeletonTable
          columnCount={10}
          rowCount={7}
          headers={[
            "#",
            "Plan ID",
            "Plan Name",
            "Device Type",
            "Plan Type",
            "Total Charge",
            "Up Front Charge",
            "EMI Amount",
            "Status",
            "Action",
          ]}
        />
      );
    }

    if (error) {
      return (
        <ErrorCard
          onRetryClick={mutate}
          title="Error loading charging plans"
          message="Failed to load charging plan data. Please try again."
        />
      );
    }

    if (chargingPlanData.length === 0) {
      return (
        <EmptyCard
          title="No charging plans found"
          message={
            searchTerm || planStatus
              ? "No charging plans found matching your search criteria."
              : "No charging plan data available. Create your first charging plan."
          }
        />
      );
    }

    return (
      <table className="inventory-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Plan ID</th>
            <th>Plan Name</th>
            <th>Device Type</th>
            <th>Plan Type</th>
            <th>Total Charge</th>
            <th>Up Front Charge</th>
            <th>EMI Amount</th>
            <th>Status</th>
            {localUserType !== "agency_user" && <th>Action</th>}
          </tr>
        </thead>
        <tbody>
          {chargingPlanData.map((plan, index) => (
            <tr key={plan.scheme_id} className="table-row">
              <td>{(page - 1) * DEFAULT_PAGE_SIZE + index + 1}</td>
              <td>{plan.scheme_id}</td>
              <td title={plan.name || ""}>{plan.name}</td>
              <td title={plan.device_model__name || ""}>
                {plan.device_model__name}
              </td>
              <td>{formatPlanType(plan.scheme_type)}</td>
              <td>
                {formatCurrency(
                  calculateTotalCharge(
                    plan.up_front_amount,
                    plan.emi_amount,
                    plan.emi_count
                  )
                )}
              </td>
              <td>{formatCurrency(plan.up_front_amount)}</td>
              <td>{formatEmiAmount(plan.emi_amount, plan.emi_count)}</td>
              <td>
                <div className={getStatusBadgeClass(plan.is_active)}>
                  <div className={getStatusBadgeIconClass(plan.is_active)}>
                    <CheckIcon style={{ fontSize: 12 }} />
                  </div>{" "}
                  <span className="plan-status-badge-text">
                    {getStatusText(plan.is_active)}
                  </span>
                </div>
              </td>
              {localUserType !== "agency_user" && (
                <td>
                  <button
                    className="edit-btn"
                    onClick={() => handleEditPlan(plan)}
                  >
                    <img src={EditIcon} alt="Edit" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="inventory-content-container">
      <div className="inventory-table-container">{renderTableContent()}</div>

      {totalCount > 0 && !loading && !error && (
        <PaginationBar
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          totalCount={totalCount}
        />
      )}

      {showEditModal && selectedPlan && (
        <CreateChargingPlanModal
          showChargingPlanCreateModal={showEditModal}
          setShowChargingPlanCreateModal={setShowEditModal}
          isDetailsMode={true}
          planData={selectedPlan}
          onPlanCreated={() => {
            mutate();
            if (onPlanCreated) {
              onPlanCreated();
            }
          }}
        />
      )}
    </div>
  );
};

export default ChargingPlanList;
